// Locale-aware formatting. Gaps (null) are shown as a dash, never as zero.

const DASH = "–";

export function formatNumber(
  value: number | null | undefined,
  locale: string,
  digits?: number,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return DASH;
  }
  const d =
    digits ??
    (Math.abs(value) >= 1000
      ? 0
      : Math.abs(value) >= 100
        ? 1
        : Math.abs(value) >= 10
          ? 2
          : 3);
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: d,
    minimumFractionDigits: digits ?? 0,
  }).format(value);
}

/** 0.42 → "42 %". */
export function formatShare(
  value: number | null | undefined,
  locale: string,
): string {
  return value === null || value === undefined
    ? DASH
    : new Intl.NumberFormat(locale, {
        style: "percent",
        maximumFractionDigits: 0,
      }).format(value);
}

export function formatDateTime(
  iso: string | Date | null | undefined,
  locale: string,
): string {
  if (!iso) {
    return DASH;
  }
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(iso));
}

/** A calendar day ("4.11.2026"), for dates without a time such as the end of a grant. */
export function formatDate(iso: string | Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: "short" }).format(
    new Date(iso),
  );
}

export function formatTime(iso: string | Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}

/** "2 Nov 2026", "1–30 Nov 2026", "1 Jan – 2 Nov 2026"; `to` is exclusive. */
export function formatRange(from: Date, to: Date, locale: string): string {
  const last = new Date(to.getTime() - 1);
  const fmt = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return from.toDateString() === last.toDateString()
    ? fmt.format(from)
    : fmt.formatRange(from, last);
}

export interface TimeTick {
  at: Date;
  options: Intl.DateTimeFormatOptions;
}

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

/**
 * Axis ticks on calendar boundaries of the local time zone: hours within a day or two, midnights over days and
 * weeks, month starts over months. Never more than about eight labels.
 */
export function timeTicks(from: Date, to: Date): TimeTick[] {
  const span = to.getTime() - from.getTime();
  const out: TimeTick[] = [];
  if (span <= 2 * DAY) {
    const step = span <= DAY ? 3 : 6;
    const d = new Date(
      from.getFullYear(),
      from.getMonth(),
      from.getDate(),
      Math.ceil(from.getHours() / step) * step,
    );
    for (; d < to; d.setHours(d.getHours() + step)) {
      out.push({
        at: new Date(d),
        options: { hour: "2-digit", minute: "2-digit" },
      });
    }
    return out;
  }
  if (span <= 70 * DAY) {
    const every = Math.max(1, Math.ceil(span / DAY / 8));
    const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    if (d < from) {
      d.setDate(d.getDate() + 1);
    }
    const options: Intl.DateTimeFormatOptions =
      span <= 10 * DAY
        ? { weekday: "short", day: "numeric" }
        : { day: "numeric", month: "short" };
    for (; d < to; d.setDate(d.getDate() + every)) {
      out.push({ at: new Date(d), options });
    }
    return out;
  }
  const months = Math.round(span / (30 * DAY));
  const every = Math.max(1, Math.ceil(months / 8));
  const d = new Date(from.getFullYear(), from.getMonth(), 1);
  if (d < from) {
    d.setMonth(d.getMonth() + 1);
  }
  for (; d < to; d.setMonth(d.getMonth() + every)) {
    out.push({ at: new Date(d), options: { month: "short", year: "2-digit" } });
  }
  return out;
}

/** How long ago, e.g. "5 min ago", for freshness of the latest values. */
export function formatAgo(
  iso: string | null | undefined,
  locale: string,
  now: Date = new Date(),
): string {
  if (!iso) {
    return DASH;
  }
  const seconds = Math.round((new Date(iso).getTime() - now.getTime()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale, {
    numeric: "auto",
    style: "short",
  });
  const abs = Math.abs(seconds);
  if (abs < 60) {
    return rtf.format(seconds, "second");
  }
  if (abs < 3600) {
    return rtf.format(Math.round(seconds / 60), "minute");
  }
  if (abs < 86400) {
    return rtf.format(Math.round(seconds / 3600), "hour");
  }
  return rtf.format(Math.round(seconds / 86400), "day");
}

/** Values older than this are shown as stale (grey), as in the prototype's freshness marks. */
export const STALE_AFTER_MS = 15 * 60 * 1000;

export function isStale(
  iso: string | null | undefined,
  now: Date = new Date(),
): boolean {
  return !iso || now.getTime() - new Date(iso).getTime() > STALE_AFTER_MS;
}
